import {
  createThemeEvaluator,
  type ThemeEvaluator,
} from '../internal/styles/themeEvaluator';
import type {
  BaseTheme,
  JoinedComponentTheme,
  StructuredTheme,
  ThemeVersion,
  TwoLevelDeepMerge,
} from '../themes/types';

const mergeThemes = <A extends BaseTheme, B extends BaseTheme>(
  a: A,
  b: B
): TwoLevelDeepMerge<A, B> => {
  const result = {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  keys.forEach((key) => {
    if (a[key] && b[key]) {
      if (Array.isArray(a[key]) || Array.isArray(b[key])) {
        result[key] = b[key] ?? a[key];
      } else {
        result[key] = { ...a[key], ...b[key] };
      }
    } else if (a[key]) {
      result[key] = a[key];
    } else if (b[key]) {
      result[key] = b[key];
    }
  });
  return result as TwoLevelDeepMerge<A, B>;
};

const parseThemeValue = (
  tokens: unknown,
  theme: unknown,
  evaluator: ThemeEvaluator
) => {
  if (Array.isArray(tokens)) {
    return tokens.map((item) => parseThemeValue(item, theme, evaluator));
  } else if (!tokens) {
    return tokens;
  } else if (typeof tokens === 'object') {
    return Object.keys(tokens).reduce((acc, key) => {
      acc[key] = parseThemeValue(tokens[key], theme, evaluator);
      return acc;
    }, {});
  } else if (typeof tokens === 'function') {
    return tokens(theme);
  }
  return evaluator.evaluate(theme, tokens);
};

const parseComponentTheme = (
  tokens: Record<string, unknown>,
  theme: Record<string, unknown>
): Record<string, unknown> => {
  return Object.keys(tokens).reduce((acc, key) => {
    const themeEvaluator = createThemeEvaluator(key);
    acc[key] = parseThemeValue(tokens[key], theme, themeEvaluator);
    return acc;
  }, {});
};

const joinComponentThemes = <C extends BaseTheme, T extends BaseTheme>(
  components: C,
  theme: T
): JoinedComponentTheme<C> => {
  return Object.keys(components).reduce((componentsTheme, componentName) => {
    const componentTokens = parseComponentTheme(
      components[componentName],
      theme
    );
    Object.keys(componentTokens).forEach((scale) => {
      if (!componentsTheme[scale]) {
        componentsTheme[scale] = {};
      }
      componentsTheme[scale][componentName] = componentTokens[scale];
    });
    return componentsTheme;
  }, {} as JoinedComponentTheme<C>);
};

export const createTheme = <T extends BaseTheme, C extends BaseTheme>(
  version: ThemeVersion,
  theme: T,
  components: C
): StructuredTheme<T, C> => {
  return {
    version,
    ...theme,
    components: mergeThemes(theme, joinComponentThemes(components, theme)),
  } as StructuredTheme<T, C>;
};
